import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { BarChart } from "../components/BarChart";
import { Empty, ErrorBox, PageHead, ResultBadge } from "../components/Bits";
import { Badge, Card, LinkButton, StatCard, Table } from "../design-system/components";
import { endpoints } from "../lib/api";
import { useCatalog } from "../lib/catalog";
import { formatDate, pct, recordText } from "../lib/format";
import { useSession } from "../lib/session";

export function OverviewPage() {
  const analytics = useQuery({ queryKey: ["analytics"], queryFn: () => endpoints.analytics() });
  const games = useQuery({ queryKey: ["games"], queryFn: endpoints.games });
  const { name } = useCatalog();
  const { profile, scopeUserId } = useSession();
  if (analytics.error) return <ErrorBox error={analytics.error} />;
  const a = analytics.data;
  const recent = (games.data?.games ?? []).slice(0, 8);
  const weekly = a?.overall.weekly.slice(-12) ?? [];

  return (
    <div className="rk-stack">
      <PageHead
        title="Overview"
        subtitle="How your decks are doing across every game you've uploaded."
        actions={<LinkButton href="/upload">Upload a log</LinkButton>}
      />
      {a && a.overall.record.games === 0 ? (
        <Card>
          <Empty title="No games yet">
            <p>Export a battle log from TCG Live and upload it to start tracking.</p>
            <LinkButton href="/upload">Upload your first log</LinkButton>
          </Empty>
        </Card>
      ) : null}
      <div className="rk-statgrid">
        <StatCard
          label="Win rate"
          value={pct(a?.overall.winRate)}
          description={
            a ? `${recordText(a.overall.record)} over ${a.overall.record.games} games` : undefined
          }
          trend={weekly.map((w) => (w.games === 0 ? 0 : (w.wins / w.games) * 100))}
        />
        <StatCard
          label="Going first"
          value={pct(
            a && a.overall.firstRecord.games > 0
              ? (a.overall.firstRecord.wins / a.overall.firstRecord.games) * 100
              : null,
          )}
          description={
            a
              ? `${recordText(a.overall.firstRecord)} · second ${recordText(a.overall.secondRecord)}`
              : undefined
          }
        />
        <StatCard
          label="Avg. turns"
          value={a?.overall.avgTurns ?? "—"}
          description="Turn headers per game"
        />
        <StatCard
          label="Prizes taken / lost"
          value={a ? `${a.overall.avgPrizesTaken ?? "—"} / ${a.overall.avgPrizesLost ?? "—"}` : "—"}
          description="Average per game"
        />
      </div>
      {(() => {
        const history = a?.overall.eloHistory ?? [];
        const latest = history[history.length - 1];
        const shown = scopeUserId === undefined ? profile?.elo : undefined;
        const elo = shown?.elo ?? latest?.elo;
        if (elo === undefined) return null;
        const values = history.map((h) => h.elo);
        const min = Math.min(...values);
        const max = Math.max(...values);
        const trend = values.map((v) => (max === min ? 60 : ((v - min) / (max - min)) * 90 + 10));
        const last = latest?.delta ?? 0;
        return (
          <div className="rk-statgrid">
            <StatCard
              label="Competitive Elo"
              value={elo}
              description={`${shown?.seasonName ?? latest?.seasonId ?? ""} · from the newest log`}
              change={
                latest
                  ? { value: `${Math.abs(last)} last game`, direction: last >= 0 ? "up" : "down" }
                  : undefined
              }
              trend={trend.length > 1 ? trend.slice(-16) : undefined}
            />
          </div>
        );
      })()}
      <div className="rk-split rk-split--wide">
        <Card title="Games per week" description="Wins highlighted within each week.">
          {weekly.length === 0 ? (
            <Empty title="Nothing to chart yet" />
          ) : (
            <BarChart
              ariaLabel="Games per week with wins"
              bars={weekly.map((w) => ({
                label: w.week.slice(5),
                title: w.week,
                value: w.games,
                secondary: w.wins,
              }))}
            />
          )}
        </Card>
        <Card
          title="Opponent archetypes"
          description="Most common matchups and your record against them."
        >
          {a && a.overall.topOpponents.length > 0 ? (
            <ul className="pr-list">
              {a.overall.topOpponents.map((o) => (
                <li key={o.archetype}>
                  <span>{o.archetype}</span>
                  <span>{recordText(o.record)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="pr-muted" style={{ fontSize: 14 }}>
              Matchups appear once games are uploaded.
            </p>
          )}
          {a && a.overall.mostPrized.length > 0 && (
            <>
              <h4 style={{ marginTop: 16, marginBottom: 8, fontSize: 13 }}>Most often prized</h4>
              <ul className="pr-list">
                {a.overall.mostPrized.slice(0, 5).map((p) => (
                  <li key={p.cardId}>
                    <span>{name({ cardId: p.cardId })}</span>
                    <span>{p.count}×</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card>
      </div>
      <div className="rk-split">
        <Card title="Decks" description="Record per deck." flush>
          {a && a.decks.length > 0 ? (
            <Table>
              <thead>
                <tr>
                  <th>Deck</th>
                  <th className="num">Games</th>
                  <th className="num">Win rate</th>
                  <th className="num">1st / 2nd</th>
                </tr>
              </thead>
              <tbody>
                {a.decks.map((d) => (
                  <tr key={d.deckId}>
                    <td>
                      <Link to={`/decks/${d.deckId}`}>{d.name}</Link>
                    </td>
                    <td className="num">{d.record.games}</td>
                    <td className="num">{pct(d.winRate)}</td>
                    <td className="num">
                      {recordText(d.firstRecord)} / {recordText(d.secondRecord)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <Empty title="No decks yet" />
          )}
        </Card>
        <Card
          title="Recent games"
          flush
          actions={
            <LinkButton href="/games" variant="ghost" size="sm">
              All games
            </LinkButton>
          }
        >
          {recent.length === 0 ? (
            <Empty title="No games yet" />
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Opponent</th>
                  <th>Deck</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((g) => (
                  <tr key={g.gameId}>
                    <td>
                      <Link to={`/games/${g.gameId}`}>{formatDate(g.playedAt)}</Link>
                    </td>
                    <td>
                      {g.opponentName ?? "?"}{" "}
                      {g.opponentArchetype ? (
                        <Badge variant="outline">{g.opponentArchetype}</Badge>
                      ) : null}
                    </td>
                    <td>{g.deckName ?? <span className="pr-muted">—</span>}</td>
                    <td>
                      <ResultBadge game={g} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </div>
    </div>
  );
}
